import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { IPressableProps, Pressable } from "native-base";
import { ColorValue, View, ViewProps, ViewStyle } from "react-native";

export interface PressableWrapProps {
  children: React.ReactElement<any>;
  triggerProps?: any;
  colors?: {
    onHoverIn: ColorValue;
    onHoverOut: ColorValue;
  };
  onPress: () => any;
  style?: ViewProps["style"];
  setColor?: (x: ColorValue) => any;
  PressableProps?: IPressableProps;
  activeOpacity?: number | undefined;
  onHoverIn?: () => any;
  onHoverOut?: () => any;

  /** optional: если хочешь отдельный обработчик именно для middle click */
  onMiddlePress?: () => any;
}

/**
 * ✅ поддерживает middle mouse click на web
 * ✅ forwardRef сохранён: <PressableWrap ref={someRef} ... />
 */
export const PressableWrap = React.forwardRef<View, PressableWrapProps>(
  (
    {
      triggerProps = {},
      children,
      colors = {
        onHoverIn: "#68ABFE",
        onHoverOut: "#1E1E1E",
      },
      onPress,
      style = {},
      setColor,
      activeOpacity,
      onHoverIn,
      onHoverOut,
      PressableProps = {},
      onMiddlePress,
    },
    ref
  ) => {
    const [currentOpacity, setOpacity] = useState(1);

    // внутренний ref — чтобы мы точно могли подписаться на DOM-элемент
    const innerRef = useRef<any>(null);

    // пробрасываем наружу тот же ref
    useImperativeHandle(ref, () => innerRef.current);

    // ✅ middle mouse support for RN Web
    useEffect(() => {
      const node = innerRef.current;

      // RN native: тут node не DOM и addEventListener не будет
      if (!node || typeof node.addEventListener !== "function") return;

      const handler = (e: MouseEvent) => {
        // middle button
        if (e.button === 1) {
          // чтобы не включался browser autoscroll
          e.preventDefault();

          // если хочешь ту же opacity-анимацию и на middle — включи:
          if (activeOpacity) {
            setOpacity(activeOpacity);
            window.setTimeout(() => setOpacity(1), 400);
          }

          // отдельный хук, если нужен
          if (onMiddlePress) onMiddlePress();
          else onPress?.();
        }
      };

      // auxclick — наиболее “правильное” событие для middle
      node.addEventListener("auxclick", handler);
      // fallback: если где-то auxclick не срабатывает
      node.addEventListener("mousedown", handler);

      return () => {
        node.removeEventListener("auxclick", handler);
        node.removeEventListener("mousedown", handler);
      };
    }, [onPress, onMiddlePress, activeOpacity]);

    return (
      <Pressable
        ref={innerRef}
        {...PressableProps}
        {...triggerProps}
        onPress={() => {
          if (activeOpacity) {
            setOpacity(activeOpacity);
            setTimeout(() => setOpacity(1), 400);
          }
          onPress?.();
        }}
        style={[style, { opacity: currentOpacity }]}
        onHoverIn={() => {
          if (setColor) setColor(colors.onHoverIn);
          onHoverIn?.();
        }}
        onHoverOut={() => {
          if (setColor) setColor(colors.onHoverOut);
          onHoverOut?.();
        }}
      >
        {children}
      </Pressable>
    );
  }
);

PressableWrap.displayName = "PressableWrap";

/** --- Opacity wrap --- */
interface PressableOpacityWrapProps {
  children: React.ReactElement<any>;
  onPress: () => any;
  style?: ViewStyle;
  PressableProps?: IPressableProps;
  activeOpacity?: number;

  /** optional */
  onMiddlePress?: () => any;
}

export const PressableOpacityWrap: React.FC<PressableOpacityWrapProps> = ({
  children,
  onPress,
  style = {},
  PressableProps = {},
  activeOpacity,
  onMiddlePress,
}) => {
  const [currentOpacity, setOpacity] = useState(1);
  const innerRef = useRef<any>(null);

  useEffect(() => {
    const node = innerRef.current;
    if (!node || typeof node.addEventListener !== "function") return;

    const handler = (e: MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault();

        if (activeOpacity) {
          setOpacity(activeOpacity);
          window.setTimeout(() => setOpacity(1), 400);
        }

        if (onMiddlePress) onMiddlePress();
        else onPress?.();
      }
    };

    node.addEventListener("auxclick", handler);
    node.addEventListener("mousedown", handler);

    return () => {
      node.removeEventListener("auxclick", handler);
      node.removeEventListener("mousedown", handler);
    };
  }, [onPress, onMiddlePress, activeOpacity]);

  return (
    <Pressable
      ref={innerRef}
      {...PressableProps}
      onPress={() => {
        if (activeOpacity) {
          setOpacity(activeOpacity);
          setTimeout(() => setOpacity(1), 400);
        }
        onPress?.();
      }}
      style={[style, { opacity: currentOpacity }]}
    >
      {children}
    </Pressable>
  );
};

export default PressableWrap;